/**
 * useHeaderState Hook
 *
 * @fileoverview
 * Manages header design state based on:
 * - Contentful page configuration (future)
 * - Current scroll position
 * - Page type (PDP, Search, etc.)
 *
 * @see task-sticky-header.md for requirements
 */

import {useState, useEffect, useMemo, useCallback, useRef} from 'react';
import {useLocation} from 'react-router-dom';

import {
  HEADER_DESIGN_STATE,
  HEADER_STYLE,
  FORCE_SOLID_PAGES,
  DISABLE_STICKY_PAGES,
  STICKY_SCROLL_THRESHOLD,
  DEFAULT_HEADER_STATE,
} from '@amblique/king-living/app/constants/header';

/** Pixels before toggling scroll direction — lower = snappier hide-on-scroll (header / chrome). */
const SCROLL_DIR_THRESHOLD = 4;

/**
 * When {@link lockBodyScrollFreeze} sets `body` to `position: fixed` with `top: -Npx`,
 * `window.scrollY` is often `0` until unlock — derive scroll from `body.style.top` when fixed.
 *
 * @returns {number}
 */
function getEffectiveWindowScrollY() {
  if (typeof window === 'undefined') {
    return 0;
  }

  const body = document.body;

  if (body?.style?.position === 'fixed') {
    const topPx = parseFloat(body.style.top);

    if (!Number.isNaN(topPx) && topPx < 0) {
      return Math.round(-topPx);
    }
  }

  return window.scrollY ?? document.documentElement?.scrollTop ?? 0;
}

/**
 * Hook to manage header design state
 *
 * @param {string} [configuredState] - Configured state from Contentful ('transparent' | 'gradient' | 'solid')
 * @returns {Object} Header state object
 * @returns {string} returns.headerState - Current header design state
 * @returns {string} returns.headerStyle - Current style for CSS ('light' | 'dark')
 * @returns {string} returns.originalState - Original configured state
 * @returns {boolean} returns.isSticky - Whether header is in sticky mode
 * @returns {number} returns.scrollY - Current scroll position
 * @returns {boolean} returns.forcesSolid - Whether page forces solid header
 * @returns {boolean} returns.stickyDisabled - Whether sticky is disabled for this page
 * @returns {string} returns.scrollDirection - Current scroll direction ('up' | 'down')
 *
 * @example
 * const {headerState, headerStyle, isSticky} = useHeaderState('transparent');
 */
export const useHeaderState = (configuredState = DEFAULT_HEADER_STATE) => {
  const [scrollY, setScrollY] = useState(0);
  const [isSticky, setIsSticky] = useState(false);
  const [scrollDirection, setScrollDirection] = useState('up');
  const lastScrollYRef = useRef(0);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    const currentScrollY = getEffectiveWindowScrollY();
    const delta = currentScrollY - lastScrollYRef.current;

    setScrollY(currentScrollY);
    setIsSticky(currentScrollY > STICKY_SCROLL_THRESHOLD);

    if (Math.abs(delta) >= SCROLL_DIR_THRESHOLD) {
      setScrollDirection(delta > 0 ? 'down' : 'up');
      lastScrollYRef.current = currentScrollY;
    }
  }, []);

  // Scroll listener
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    window.addEventListener('scroll', handleScroll, {passive: true});
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Reset scroll state on route change
  useEffect(() => {
    setScrollY(0);
    setIsSticky(false);
    setScrollDirection('up');
    lastScrollYRef.current = 0;
  }, [location.pathname]);

  // Determine if page forces solid header (PDP, Search results)
  const forcesSolid = useMemo(() => {
    return FORCE_SOLID_PAGES.some((path) => location.pathname.includes(path));
  }, [location.pathname]);

  // Determine if sticky is disabled (Category pages)
  const stickyDisabled = useMemo(() => {
    return DISABLE_STICKY_PAGES.some((path) => location.pathname.includes(path));
  }, [location.pathname]);

  // Calculate current header state
  const headerState = useMemo(() => {
    // Force solid for specific pages (PDP, Search)
    if (forcesSolid) return HEADER_DESIGN_STATE.SOLID;

    // Sticky = solid (when scrolled past threshold on eligible pages)
    if (isSticky && !stickyDisabled) return HEADER_DESIGN_STATE.SOLID;

    // Return configured state from Contentful/code
    return configuredState;
  }, [forcesSolid, isSticky, stickyDisabled, configuredState]);

  // Get header style (light/dark) for CSS
  // Gradient = dark style (white icons/text), others = light style (black icons/text)
  const headerStyle = useMemo(() => {
    return headerState === HEADER_DESIGN_STATE.GRADIENT ?
      HEADER_STYLE.DARK :
      HEADER_STYLE.LIGHT;
  }, [headerState]);

  // Original state (for reference when scroll back to top)
  const originalState = useMemo(() => {
    if (forcesSolid) return HEADER_DESIGN_STATE.SOLID;

    return configuredState;
  }, [forcesSolid, configuredState]);

  return {
    headerState, // 'transparent' | 'gradient' | 'solid'
    headerStyle, // 'light' | 'dark'
    originalState, // Original configured state
    isSticky, // true when scrollY > threshold
    scrollY, // Current scroll position
    scrollDirection, // 'up' | 'down'
    forcesSolid, // true for PDP, Search pages
    stickyDisabled, // true for Category pages
  };
};

export default useHeaderState;
